import { ChangeEvent, useContext, useState } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS
} from "components/DesignComponents/Button";
import Dialog from "components/DesignComponents/Dialog";
import TableContext from "context/table/tableContext";
import { useApi } from "shared/hooks/useApi";
import { useMediaQueries } from "shared/hooks/useMediaQueries";
import { maxDialogWidth } from "shared/styles";
import { ENDPOINT_PATH_SEGMENTS, ENDPOINTS } from "shared/types/enums";

import {
    BACK,
    CLOSE,
    INVALID_DEVICE_LOCK_CODE,
    LIMIT_REACHED
} from "./constants";
import GenerateKeyContent from "./GenerateKeyContent";
import { GenerateUnlockKeyDialogProps } from "./types";

const GenerateUnlockKeyDialog = ({
    isOpen,
    closeDialog
}: GenerateUnlockKeyDialogProps) => {
    const {
        detailsData: { imei }
    } = useContext(TableContext);

    const { t } = useTranslation();
    const { toMd, fromMd } = useMediaQueries();
    const { getCriticalMessage, handleResponse, postData } = useApi();

    const [isLoading, setIsLoading] = useState(false);
    const [lockCode, setLockCode] = useState("");
    const [unlockKey, setUnlockKey] = useState("");
    const [isInvalidLockCode, setIsInvalidLockCode] = useState(false);
    const [isLimitReached, setIsLimitReached] = useState(false);

    const handleLockCodeValueChange = (e: ChangeEvent<HTMLInputElement>) => {
        setIsInvalidLockCode(false);
        setLockCode(e.target.value.trim());
    };

    const handleSecondaryBtnClick = () => {
        if (unlockKey) {
            setUnlockKey("");
        } else {
            closeDialog();
        }
    };

    const submitLockCode = async () => {
        try {
            setIsLoading(true);

            const { data } = await postData(
                `${ENDPOINTS.Devices}${ENDPOINT_PATH_SEGMENTS.UnlockKeys}`,
                {
                    device_imei: imei,
                    lock_code: lockCode
                }
            );

            setUnlockKey(data);
        } catch (error) {
            const { message } = getCriticalMessage(error);

            const isLimitReached = message === LIMIT_REACHED;
            const isInvalidLockCode = message === INVALID_DEVICE_LOCK_CODE;

            if (isLimitReached) {
                setLockCode("");
                setIsLimitReached(true);
            } else if (isInvalidLockCode) {
                setIsInvalidLockCode(true);
            } else {
                handleResponse(error);
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog
            isTitleSeparator
            isActionsSeparator
            title={t("Dialog##unlocker##generate unlock key")}
            description={t("Dialog##unlocker##generate unlock key description")}
            open={isOpen}
            close={closeDialog}
            submit={submitLockCode}
            actions={
                <>
                    <Button
                        fullWidth
                        color={BUTTON_COLORS.Secondary}
                        size={BUTTON_SIZES.Normal}
                        variant={BUTTON_VARIANTS.TextOnly}
                        disabled={isLoading}
                        onClick={handleSecondaryBtnClick}
                    >
                        {t(`Button##${unlockKey ? BACK : CLOSE}`)}
                    </Button>

                    {!unlockKey && (
                        <Button
                            fullWidth
                            color={BUTTON_COLORS.Primary}
                            size={BUTTON_SIZES.Normal}
                            variant={BUTTON_VARIANTS.TextOnly}
                            type="submit"
                            isLoading={isLoading}
                            disabled={isLimitReached || lockCode.length === 0}
                        >
                            {t("Button##generate")}
                        </Button>
                    )}
                </>
            }
            slotProps={{
                transition: {
                    onExited: () => {
                        setIsLoading(false);
                        setLockCode("");
                        setUnlockKey("");
                        setIsInvalidLockCode(false);
                        setIsLimitReached(false);
                    }
                }
            }}
            extendToMaxWidth={toMd}
            css={css(fromMd && maxDialogWidth)}
        >
            <GenerateKeyContent
                isLimitReached={isLimitReached}
                isInvalidLockCode={isInvalidLockCode}
                isLoading={isLoading}
                unlockKey={unlockKey}
                lockCode={lockCode}
                handleLockCodeValueChange={handleLockCodeValueChange}
            />
        </Dialog>
    );
};

export default GenerateUnlockKeyDialog;
